/**
 * WebSocketContext
 *
 * Real-time task progress, credit updates and notifications over one socket.
 * Split by concern (de-slop FE-7) so a message only re-renders the consumers
 * that care about it:
 *
 * - connection layer   → `websocket/connection.ts` (lifecycle, auth, backoff,
 *                         heartbeat, polling fallback) exposed via
 *                         `useWebSocketConnection()`; re-renders on state changes only
 * - task events        → `websocket/taskEventStore.ts`, read per task id via
 *                         `useTaskProgress()` (useSyncExternalStore), or polled via
 *                         `useTaskEvents().getTaskProgress()`; the API object is stable
 * - user-level events  → `websocket/userEventStore.ts` (credit updates,
 *                         notifications) via `useCreditUpdates()` /
 *                         `useNotificationEvents()`
 *
 * Per-user state in both stores is reset when the session identity changes
 * (logout, user switch — FE-20).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { AppState, Platform } from 'react-native';
import { getAccessToken, API_BASE_URL, USE_COOKIES, refreshSession, notifyUnauthorized } from '../services/api';
import { shouldResetWebsocketSession, websocketSessionKey } from './websocketSession';
import { taskErrorDetails, type TaskErrorDetails } from './taskErrorProgress';
import { WebSocketConnection, type ConnectionParams } from './websocket/connection';
import { STALL_CHECK_INTERVAL_MS, TaskEventStore } from './websocket/taskEventStore';
import { UserEventStore, type UserEventSnapshot } from './websocket/userEventStore';
import { routeWebSocketMessage } from './websocket/messageRouter';
import { bindPageLifecycle } from './websocket/pageLifecycle';
import {
  warnDebug,
  type ConnectionState,
  type CreditUpdateEvent,
  type NotificationEvent,
  type TaskProgress,
} from './websocket/types';

export type { ConnectionState, CreditUpdateEvent, NotificationEvent, TaskProgress };

// =============================================================================
// Context values
// =============================================================================

export interface WebSocketConnectionValue {
  connectionState: ConnectionState;
  isConnected: boolean;
  useFallback: boolean;
}

/** Stable for the provider's lifetime — never triggers a re-render. */
export interface TaskEventsValue {
  subscribe: (taskId: string) => void;
  unsubscribe: (taskId: string) => void;
  getTaskProgress: (taskId: string) => TaskProgress | null;
  clearTaskProgress: (taskId: string) => void;
  /** Change listener for one task id (useSyncExternalStore subscribe). */
  listenTask: (taskId: string, listener: () => void) => () => void;
  onStreamDelta: (taskId: string, cb: (text: string) => void) => () => void;
  onStreamError: (taskId: string, cb: (error: string) => void) => () => void;
  onStreamReset: (taskId: string, cb: () => void) => () => void;
}

const ConnectionContext = createContext<WebSocketConnectionValue | undefined>(undefined);
const TaskEventsContext = createContext<TaskEventsValue | undefined>(undefined);
const UserEventsContext = createContext<UserEventStore | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

interface WebSocketProviderProps {
  children: React.ReactNode;
  /** Disable WebSocket connection (for testing or specific scenarios) */
  disabled?: boolean;
  /** Current user id; per-user state is reset when it changes. */
  userKey?: string | number | null;
}

// Build WebSocket connection parameters
async function getConnectionParams(): Promise<ConnectionParams | null> {
  // Convert http(s) to ws(s)
  const wsBase = API_BASE_URL.replace(/^http/, 'ws');
  const wsUrl = `${wsBase}/ws`;

  // For non-cookie auth, send the token as a WebSocket subprotocol instead of in the URL.
  if (!USE_COOKIES) {
    const token = await getAccessToken();
    if (!token) {
      warnDebug('[WebSocket] No auth token available');
      return null;
    }
    return { url: wsUrl, protocols: ['bearer', token] };
  }

  // Cookie-based auth - just connect (cookies sent automatically)
  return { url: wsUrl };
}

interface Runtime {
  connection: WebSocketConnection;
  tasks: TaskEventStore;
  user: UserEventStore;
  taskApi: TaskEventsValue;
}

export function WebSocketProvider({
  children,
  disabled = false,
  userKey = null,
}: WebSocketProviderProps) {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [useFallback, setUseFallback] = useState(false);

  // Stores + connection live for the provider's lifetime.
  const runtimeRef = useRef<Runtime | null>(null);
  if (!runtimeRef.current) {
    const tasks = new TaskEventStore();
    const user = new UserEventStore();
    const connection = new WebSocketConnection({
      createSocket: (url, protocols) =>
        (protocols ? new WebSocket(url, protocols) : new WebSocket(url)) as any,
      getConnectionParams,
      refreshSession,
      notifyUnauthorized,
      onMessage: (message) => { routeWebSocketMessage(message, { tasks, user }); },
      onStateChange: setConnectionState,
      onFallbackChange: setUseFallback,
      getSubscribedTaskIds: () => tasks.subscribedTaskIds(),
    });
    const taskApi: TaskEventsValue = {
      subscribe: (taskId) => {
        tasks.track(taskId);
        connection.sendIfOpen({ type: 'subscribe', task_id: taskId });
      },
      unsubscribe: (taskId) => {
        tasks.untrack(taskId);
        connection.sendIfOpen({ type: 'unsubscribe', task_id: taskId });
      },
      getTaskProgress: tasks.getTaskProgress,
      clearTaskProgress: (taskId) => tasks.clear(taskId),
      listenTask: tasks.listen,
      onStreamDelta: tasks.onStreamDelta,
      onStreamError: tasks.onStreamError,
      onStreamReset: tasks.onStreamReset,
    };
    runtimeRef.current = { connection, tasks, user, taskApi };
  }
  const { connection, tasks, user, taskApi } = runtimeRef.current;
  // Applied during render (idempotent) so effects that run before the mount
  // effect below — e.g. the initial visibility check — already see it.
  connection.setDisabled(disabled);

  // Reset all per-user state when the session identity changes (FE-20).
  const sessionKey = websocketSessionKey(disabled, userKey);
  const sessionKeyRef = useRef(sessionKey);
  useEffect(() => {
    if (!shouldResetWebsocketSession(sessionKeyRef.current, sessionKey)) return;
    sessionKeyRef.current = sessionKey;
    tasks.reset();
    user.reset();
  }, [sessionKey, tasks, user]);

  // Reconnect when the page/app becomes visible again. Registered once; it
  // reads live state from the connection, so connection-state changes never
  // re-trigger it (that used to bypass the backoff on web).
  useEffect(
    () =>
      bindPageLifecycle(connection, {
        document: Platform.OS === 'web' && typeof document !== 'undefined' ? document : null,
        appState: AppState,
      }),
    [connection]
  );

  // Connect on mount (if not disabled)
  useEffect(() => {
    if (!disabled) {
      // Arm reconnects here (not inside connect()), so AppState/visibility/
      // reconnect timers cannot silently undo an explicit disconnect().
      connection.arm();
      void connection.connect();
    }
    return () => {
      connection.disconnect();
    };
  }, [disabled, connection]);

  // Stall detection: periodically flag in-progress tasks that have gone
  // STALL_TIMEOUT_MS without any progress or still_processing message.
  useEffect(() => {
    const interval = setInterval(() => tasks.checkStalls(), STALL_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [tasks]);

  const connectionValue = useMemo<WebSocketConnectionValue>(
    () => ({ connectionState, isConnected: connectionState === 'connected', useFallback }),
    [connectionState, useFallback]
  );

  return (
    <ConnectionContext.Provider value={connectionValue}>
      <TaskEventsContext.Provider value={taskApi}>
        <UserEventsContext.Provider value={user}>{children}</UserEventsContext.Provider>
      </TaskEventsContext.Provider>
    </ConnectionContext.Provider>
  );
}

// =============================================================================
// Hooks
// =============================================================================

function useRequired<T>(context: React.Context<T | undefined>, hook: string): T {
  const value = useContext(context);
  if (!value) {
    throw new Error(`${hook} must be used within a WebSocketProvider`);
  }
  return value;
}

/** Connection state only; re-renders when the connection state changes. */
export function useWebSocketConnection(): WebSocketConnectionValue {
  return useRequired(ConnectionContext, 'useWebSocketConnection');
}

/** Task subscription API; stable, never re-renders on messages. */
export function useTaskEvents(): TaskEventsValue {
  return useRequired(TaskEventsContext, 'useTaskEvents');
}

function useUserEventSnapshot(): [UserEventSnapshot, UserEventStore] {
  const store = useRequired(UserEventsContext, 'useUserEvents');
  const snapshot = useSyncExternalStore(store.listen, store.getSnapshot, store.getSnapshot);
  return [snapshot, store];
}

/** Latest credit_update event; re-renders on user-level events only. */
export function useCreditUpdates(): { lastCreditUpdate: CreditUpdateEvent | null } {
  const [snapshot] = useUserEventSnapshot();
  return { lastCreditUpdate: snapshot.lastCreditUpdate };
}

/** Live notification state; re-renders on user-level events only. */
export function useNotificationEvents() {
  const [snapshot, store] = useUserEventSnapshot();
  return {
    unreadNotificationCount: snapshot.unreadNotificationCount,
    latestNotification: snapshot.latestNotification,
    setUnreadNotificationCount: store.setUnreadNotificationCount as React.Dispatch<React.SetStateAction<number>>,
    clearLatestNotification: store.clearLatestNotification,
  };
}

/**
 * Subscribe to task progress updates. Re-renders only when this task's entry
 * changes.
 *
 * @param taskId - The Celery task ID to track
 * @param options - Optional configuration
 * @returns Task progress state
 */
export function useTaskProgress(
  taskId: string | null | undefined,
  options?: {
    /** Called when task completes successfully */
    onComplete?: (result?: Record<string, any>) => void;
    /**
     * Called when task fails. `error` is the static English text; `details`
     * carries the stable error code / i18n key for localized display.
     */
    onError?: (error: string, details?: TaskErrorDetails) => void;
    /** Auto-clear progress after completion (default: false) */
    autoClear?: boolean;
    /** Delay before auto-clear in ms (default: 1000) */
    autoClearDelay?: number;
  }
): TaskProgress | null {
  const { subscribe, unsubscribe, getTaskProgress, clearTaskProgress, listenTask } = useTaskEvents();

  const completedRef = useRef(false);
  const erroredRef = useRef(false);

  // Subscribe when taskId changes
  useEffect(() => {
    if (!taskId) return;

    completedRef.current = false;
    erroredRef.current = false;
    subscribe(taskId);

    return () => {
      unsubscribe(taskId);
    };
  }, [taskId, subscribe, unsubscribe]);

  const listen = useCallback(
    (onChange: () => void) => (taskId ? listenTask(taskId, onChange) : () => {}),
    [taskId, listenTask]
  );
  const read = useCallback(
    () => (taskId ? getTaskProgress(taskId) : null),
    [taskId, getTaskProgress]
  );
  const progress = useSyncExternalStore(listen, read, read);

  // Handle completion callbacks
  useEffect(() => {
    if (!progress || !taskId) return;

    if (progress.isComplete && !progress.error && !completedRef.current) {
      completedRef.current = true;
      options?.onComplete?.(progress.result);

      if (options?.autoClear) {
        setTimeout(() => {
          clearTaskProgress(taskId);
        }, options.autoClearDelay ?? 1000);
      }
    }

    if (progress.error && !erroredRef.current) {
      erroredRef.current = true;
      options?.onError?.(progress.error, taskErrorDetails(progress));

      if (options?.autoClear) {
        setTimeout(() => {
          clearTaskProgress(taskId);
        }, options.autoClearDelay ?? 1000);
      }
    }
  }, [progress, taskId, options, clearTaskProgress]);

  return progress;
}
